/**
 * Weather (สภาพอากาศ): the kinds, what each looks like as plain numbers, how long a spell lasts, and
 * how likely each kind is in each zone. Data and pure functions only: the weather changes in
 * `systems/weather.ts`, through the World's `Rng`, as `World.step` runs (no timers, no Date).
 *
 * Gameplay effects: none yet (docs/systems/WEATHER.md). The numbers below only drive the picture and sound.
 */
import type { Biome } from '../map';
import { ZONES, type ZoneId } from './zones';

export type WeatherKind = 'clear' | 'cloudy' | 'rain' | 'storm' | 'fog' | 'snow';

export const WEATHER_KINDS: readonly WeatherKind[] = ['clear', 'cloudy', 'rain', 'storm', 'fog', 'snow'];

export const isWeatherKind = (v: unknown): v is WeatherKind =>
  typeof v === 'string' && (WEATHER_KINDS as readonly string[]).includes(v);

/**
 * What a kind looks like, every number 0–1, for the renderer (and the sky lane) to read:
 * - `cloud`: cloud cover (darker light, a greyer sky);
 * - `wet`: how wet the ground is (puddle shine, darker ground);
 * - `rain`, `snow`: how hard it falls;
 * - `fog`: how far the fog closes in (1: a few dozen metres);
 * - `storm`: lightning and gusts;
 * - `wind`: how far the falling streaks lean.
 */
export interface WeatherLook {
  readonly cloud: number;
  readonly wet: number;
  readonly rain: number;
  readonly snow: number;
  readonly fog: number;
  readonly storm: number;
  readonly wind: number;
}

export interface WeatherInfo extends WeatherLook {
  readonly id: WeatherKind;
  /** The Thai name on the HUD. */
  readonly name: string;
  /** A small mark next to the clock (text-presentation symbols, as the clock's sun and moon). */
  readonly mark: string;
}

export const WEATHER: Readonly<Record<WeatherKind, WeatherInfo>> = {
  clear: {
    id: 'clear',
    name: 'แจ่มใส',
    mark: '☀︎',
    cloud: 0,
    wet: 0,
    rain: 0,
    snow: 0,
    fog: 0,
    storm: 0,
    wind: 0.1,
  },
  cloudy: {
    id: 'cloudy',
    name: 'ครึ้ม',
    mark: '☁︎',
    cloud: 0.6,
    wet: 0,
    rain: 0,
    snow: 0,
    fog: 0.15,
    storm: 0,
    wind: 0.3,
  },
  rain: {
    id: 'rain',
    name: 'ฝน',
    mark: '☂︎',
    cloud: 0.8,
    wet: 0.8,
    rain: 0.6,
    snow: 0,
    fog: 0.3,
    storm: 0,
    wind: 0.3,
  },
  storm: {
    id: 'storm',
    name: 'พายุฝน',
    mark: '⛈︎',
    cloud: 1,
    wet: 1,
    rain: 1,
    snow: 0,
    fog: 0.45,
    storm: 1,
    wind: 0.8,
  },
  fog: {
    id: 'fog',
    name: 'หมอก',
    mark: '▒',
    cloud: 0.4,
    wet: 0.2,
    rain: 0,
    snow: 0,
    fog: 1,
    storm: 0,
    wind: 0,
  },
  snow: {
    id: 'snow',
    name: 'หิมะ',
    mark: '❄︎',
    cloud: 0.6,
    wet: 0,
    rain: 0,
    snow: 0.8,
    fog: 0.35,
    storm: 0,
    wind: 0.25,
  },
};

/** How long a spell of weather lasts (real seconds), and how long it takes to turn into the next. */
export const WEATHER_TIMING = {
  minSpell: 4 * 60,
  maxSpell: 10 * 60,
  /** Every new world starts clear and turns after this long (then a spell is drawn each time). */
  firstSpell: 3 * 60,
  transition: 45,
} as const;

/** Relative weights of each kind (a kind left out never comes). */
export type WeatherWeights = Partial<Record<WeatherKind, number>>;

/** By the zone's ground: green lands get rain, the snow fields snow. */
export const BIOME_WEATHER: Readonly<Record<Biome, WeatherWeights>> = {
  plains: { clear: 40, cloudy: 25, rain: 15, storm: 5, fog: 15 },
  snow: { clear: 30, cloudy: 20, snow: 35, fog: 15 },
};

/** Zones whose weather differs from their ground's. */
export const ZONE_WEATHER: Partial<Record<ZoneId, WeatherWeights>> = {
  // 烟雨, "misty rain": the starting region lives up to its name.
  yanyu: { clear: 25, cloudy: 20, rain: 25, storm: 5, fog: 25 },
  // The mountain schools sit in the clouds.
  'mist-peak': { clear: 30, cloudy: 20, rain: 15, storm: 5, fog: 30 },
};

export function weatherWeights(zone: ZoneId): WeatherWeights {
  return ZONE_WEATHER[zone] ?? BIOME_WEATHER[ZONES[zone].terrain];
}

/** A storm only grows out of grey skies or rain, never out of a clear sky or fog. */
export const STORM_AFTER: readonly WeatherKind[] = ['cloudy', 'rain'];

/**
 * The weather now: the kind it is turning into (or has turned into), the kind before, how far the
 * turn has gone (0 → 1), and the seconds left in this spell. Plain data, sent to clients as it is.
 */
export interface WeatherState {
  kind: WeatherKind;
  from: WeatherKind;
  blend: number;
  left: number;
}

export function newWeather(
  kind: WeatherKind = 'clear',
  left: number = WEATHER_TIMING.firstSpell,
): WeatherState {
  return { kind, from: kind, blend: 1, left };
}

/** The look now, blended between the kind before and the kind it is turning into. */
export function weatherLook(s: WeatherState): WeatherLook {
  const a = WEATHER[s.from];
  const b = WEATHER[s.kind];
  const t = Math.max(0, Math.min(1, s.blend));
  const m = (k: keyof WeatherLook) => a[k] + (b[k] - a[k]) * t;
  return {
    cloud: m('cloud'),
    wet: m('wet'),
    rain: m('rain'),
    snow: m('snow'),
    fog: m('fog'),
    storm: m('storm'),
    wind: m('wind'),
  };
}

/**
 * The next kind, drawn from the zone's weights with `r` in [0, 1): never the same kind again, and a
 * storm only after cloud or rain. Falls back to the current kind if nothing else can come.
 */
export function nextWeather(zone: ZoneId, now: WeatherKind, r: number): WeatherKind {
  const w = weatherWeights(zone);
  const can = WEATHER_KINDS.filter(
    (k) => k !== now && (w[k] ?? 0) > 0 && (k !== 'storm' || STORM_AFTER.includes(now)),
  );
  const total = can.reduce((s, k) => s + (w[k] ?? 0), 0);
  if (total <= 0) return now;
  let x = r * total;
  for (const k of can) if ((x -= w[k] ?? 0) < 0) return k;
  return can[can.length - 1]!;
}
